import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EtapeCochable, MotifRelance } from '@mosaik/core';
import { api, requete } from './client';
import { useReleveEtapeSuivante } from './releve-etape-suivante';
import type {
  CanalEnvoi, CommandeDetaillee, DocumentReglages, MailEnvoiLu, PageCommandes, PageJournal,
  EtatBrouillonRelance, EtatReferentiel, PageReferentiel, ProduitReferentiel, ResolutionHubspot,
  ResultatCommandeFournisseur, ResultatRelance, Sante,
} from './types';

export interface FiltresCommandes {
  q?: string;
  /** Onglet : `afaire`, `encours`, `problemes`, `archive`. */
  vue?: string;
  etape?: string;
  action?: string;
  fournisseur?: string;
  transporteur?: string;
  tri?: string;
  page?: number;
  taille?: number;
}

export const cles = {
  commandes: (f: FiltresCommandes) => ['commandes', f] as const,
  commande: (id: string) => ['commande', id] as const,
  journal: (page: number, resultat?: string) => ['journal', page, resultat] as const,
  reglages: ['reglages'] as const,
  sante: ['sante'] as const,
};

export function useCommandes(filtres: FiltresCommandes) {
  return useQuery({
    queryKey: cles.commandes(filtres),
    queryFn: () => api.get<PageCommandes>(`/api/commandes${requete(filtres as Record<string, string>)}`),
    // Garde l'ancienne page affichée pendant le chargement de la suivante :
    // le tableau ne clignote pas quand on change de filtre.
    placeholderData: (precedent) => precedent,
  });
}

export function useCommande(id: string | null) {
  return useQuery({
    queryKey: cles.commande(id ?? ''),
    queryFn: () => api.get<CommandeDetaillee>(`/api/commandes/${id}`),
    enabled: Boolean(id),
  });
}

/**
 * Les mails envoyés pour un envoi, relus dans Outlook.
 *
 * Pas de cache durable : un nouvel envoi a pu remplacer celui d'hier, et
 * c'est précisément ce qu'on vient vérifier en ouvrant la fenêtre.
 */
export function useMailsEnvoi(id: string, canal: CanalEnvoi, enabled = true) {
  return useQuery({
    queryKey: ['mails-envoi', id, canal] as const,
    queryFn: () => api.get<MailEnvoiLu[]>(`/api/commandes/${id}/envois/${canal}/mails`),
    staleTime: 0,
    enabled,
  });
}

export function useJournal(page: number, resultat?: string) {
  return useQuery({
    queryKey: cles.journal(page, resultat),
    queryFn: () => api.get<PageJournal>(`/api/journal${requete({ page, taille: 50, resultat })}`),
  });
}

export function useReglages() {
  return useQuery({ queryKey: cles.reglages, queryFn: () => api.get<DocumentReglages>('/api/reglages') });
}

export function useSante() {
  return useQuery({
    queryKey: cles.sante,
    queryFn: () => api.get<Sante>('/health'),
    refetchInterval: 60_000,
    retry: false,
  });
}

/**
 * Invalide tout ce qu'une mutation métier peut avoir changé.
 *
 * Les seuils et la date du jour font que l'action due d'une commande dépend de
 * l'état des autres : on réinvalide la liste entière plutôt que de patcher une
 * ligne et de laisser le reste du tableau périmé.
 */
function useInvalidation() {
  const client = useQueryClient();
  return (id?: string) => {
    void client.invalidateQueries({ queryKey: ['commandes'] });
    void client.invalidateQueries({ queryKey: ['journal'] });
    if (id) void client.invalidateQueries({ queryKey: cles.commande(id) });
  };
}

interface MutationCommande<C> {
  id: string;
  corps: C;
}

export function useMajExpedition() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, corps }: MutationCommande<Record<string, unknown>>) =>
      api.patch<CommandeDetaillee>(`/api/commandes/${id}/expedition`, corps),
    onSuccess: (_d, v) => invalider(v.id),
  });
}

/** Un mail de commande relu à l'écran, envoyé tel quel. */
export interface MailFournisseur {
  fournisseur: string;
  /** Adresses, séparées par des virgules. */
  destinataire: string;
  objet: string;
  /** Corps en texte brut. Mis en forme à l'envoi. */
  message: string;
}

/** Corps de `POST /commandes/:id/fournisseur`. */
export interface CorpsEnvoiFournisseur {
  operation: 'transmettre' | 'repasser';
  /** Mails relus : quand ils sont fournis, l'API envoie exactement ceux-là. */
  mails?: MailFournisseur[];
  commentaire?: string;
  /** Changements Shopify connus quand les mails ont été composés. */
  ajustementsConnus?: string[];
}

export function useEnvoiFournisseur() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, corps }: MutationCommande<CorpsEnvoiFournisseur>) =>
      api.post<ResultatCommandeFournisseur>(`/api/commandes/${id}/fournisseur`, corps),
    onSuccess: (_d, v) => invalider(v.id),
  });
}

export function useConfirmerLivraison() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, corps }: MutationCommande<{ date?: string; commentaire?: string }>) =>
      api.post<CommandeDetaillee>(`/api/commandes/${id}/livraison`, corps),
    onSuccess: (_d, v) => invalider(v.id),
  });
}

/** Corps de `POST /commandes/:id/relance`. */
export interface CorpsRelance {
  cible: 'fournisseur' | 'transporteur';
  /** Ce qu'on réclame. C'est lui que relira la colonne « Litige ». */
  motif?: MotifRelance;
  /** Fournisseur visé quand la commande en compte plusieurs. */
  fournisseur?: string;
  /** Adresses séparées par des virgules. */
  destinataire: string;
  objet: string;
  /** Corps du mail en texte brut, tel qu'il a été relu à l'écran. */
  message: string;
  commentaire?: string;
}

export function useRelance() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, corps }: MutationCommande<CorpsRelance>) =>
      api.post<ResultatRelance>(`/api/commandes/${id}/relance`, corps),
    onSuccess: (_d, v) => invalider(v.id),
  });
}

/**
 * Recherche dans le référentiel produits.
 *
 * La virgule sépare les termes, et tous doivent se retrouver — le serveur s'en
 * charge. La recherche n'est lancée qu'à partir de deux caractères utiles : à
 * vide elle remonterait les 1 700 références, ce qui n'aide personne.
 */
export function useRechercheProduits(q: string) {
  const terme = q.trim();
  const utile = terme.split(',').some((t) => t.trim().length >= 2);
  return useQuery({
    queryKey: ['referentiel', terme],
    queryFn: () => api.get<PageReferentiel>(`/api/referentiel${requete({ q: terme, taille: 25 })}`),
    enabled: utile,
    placeholderData: (precedent) => precedent,
  });
}

/**
 * État du référentiel : combien de références, combien de correspondances.
 *
 * Sert à ne pas accuser un produit quand c'est l'import qui est en retard :
 * un référentiel sans aucune correspondance se répare par `pnpm seed
 * --produits`, pas en corrigeant la commande.
 */
export function useEtatReferentiel() {
  return useQuery({
    queryKey: ['referentiel', 'etat'],
    queryFn: () => api.get<EtatReferentiel>('/api/referentiel/etat'),
    staleTime: 5 * 60_000,
  });
}

/**
 * Recharge le référentiel depuis la base.
 *
 * `pnpm seed --produits` tourne dans un autre processus : l'API sert son cache
 * jusqu'à expiration. Ce bouton referme l'écart tout de suite.
 */
export function useRechargerReferentiel() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<EtatReferentiel>('/api/referentiel/recharger'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['referentiel'] });
      void client.invalidateQueries({ queryKey: ['commandes'] });
      void client.invalidateQueries({ queryKey: ['commande'] });
    },
  });
}

/** Ajoute ou corrige une référence du référentiel produits. */
export function useEnregistrerProduit() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (corps: {
      reference: string; nom?: string; correspondance?: string | null;
      unite?: string | null; fournisseur?: string | null;
    }) => api.post<ProduitReferentiel>('/api/referentiel', corps),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['referentiel'] });
      // Les fournisseurs déduits changent : les commandes affichées aussi.
      void client.invalidateQueries({ queryKey: ['commandes'] });
      void client.invalidateQueries({ queryKey: ['commande'] });
    },
  });
}

export function useMajArticles() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, corps }: MutationCommande<{
      articles: Array<{ reference: string; quantite?: number | null }>;
    }>) => api.patch<CommandeDetaillee>(`/api/commandes/${id}/articles`, corps),
    onSuccess: (_d, v) => invalider(v.id),
  });
}

/**
 * Remet une commande livrée en cours.
 *
 * L'inverse de `useConfirmerLivraison` : sans lui, un clic de travers classait
 * une commande en archive pour de bon.
 */
export function useAnnulerLivraison() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      api.post<CommandeDetaillee>(`/api/commandes/${id}/livraison/annuler`, {}),
    onSuccess: (_d, v) => invalider(v.id),
  });
}

/**
 * Envoie un mail relu, depuis contact@.
 *
 * Le pendant de la relecture : l'application compose, l'utilisateur corrige, et
 * c'est ce texte-là qui part. Le canal n'est marqué parti que si Outlook a
 * confirmé l'envoi.
 */
export function useDeposerBrouillon() {
  const invalider = useInvalidation();
  const releverSuite = useReleveEtapeSuivante();
  return useMutation({
    mutationFn: ({ id, canal, corps }: {
      id: string;
      canal: 'tracking' | 'expedition' | 'fournisseur';
      corps: { destinataire: string; objet: string; message: string };
    }) => api.post<{ commande: CommandeDetaillee; brouillon: EtatBrouillonRelance }>(
      `/api/commandes/${id}/envois/${canal}/brouillon`, corps,
    ),
    // Le mail parti valide son étape : le relevé de la suivante part aussitôt.
    onSuccess: (r, v) => { invalider(v.id); releverSuite(r.commande); },
  });
}

/**
 * Les colis d'une commande partie en plusieurs envois : ajout, retrait, et le
 * mail relu de chacun. Le colis principal garde ses propres chemins.
 */
export function useAjouterColis() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, tracking, transporteur, sansMail = false }: {
      id: string; tracking: string; transporteur: string | null; sansMail?: boolean;
    }) => api.post<CommandeDetaillee>(`/api/commandes/${id}/colis`, { tracking, transporteur, sansMail }),
    onSuccess: (_d, v) => invalider(v.id),
  });
}

export function useRetirerColis() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, tracking }: { id: string; tracking: string }) =>
      api.delete<CommandeDetaillee>(`/api/commandes/${id}/colis/${encodeURIComponent(tracking)}`),
    onSuccess: (_d, v) => invalider(v.id),
  });
}

/** Le client ne sera pas prévenu de ce colis : « Prévenir le client du colis » s'éteint. */
export function useIgnorerMailColis() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, tracking }: { id: string; tracking: string }) =>
      api.post<CommandeDetaillee>(`/api/commandes/${id}/colis/${encodeURIComponent(tracking)}/ignorer`, {}),
    onSuccess: (_d, v) => invalider(v.id),
  });
}

export function useEnvoyerMailColis() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, tracking, corps }: {
      id: string;
      tracking: string;
      corps: { destinataire: string; objet: string; message: string };
    }) => api.post<{ commande: CommandeDetaillee; brouillon: EtatBrouillonRelance }>(
      `/api/commandes/${id}/colis/${encodeURIComponent(tracking)}/mail`, corps,
    ),
    onSuccess: (_d, v) => invalider(v.id),
  });
}

/**
 * Coche ou décoche une étape de la fiche à la main. `null` la rend à l'état
 * automatique — ce que fait aussi, côté serveur, une coche égale aux données.
 */
export function useCocherEtape() {
  const invalider = useInvalidation();
  const releverSuite = useReleveEtapeSuivante();
  return useMutation({
    mutationFn: ({ id, etape, coche }: { id: string; etape: EtapeCochable; coche: boolean | null }) =>
      api.put<CommandeDetaillee>(`/api/commandes/${id}/etapes/${etape}`, { coche }),
    onSuccess: (r, v) => { invalider(v.id); if (v.coche === true) releverSuite(r); },
  });
}

/**
 * Pose ou lève l'attente du règlement : la commande quitte « À faire » tant que
 * la facture n'est pas réglée, et y revient d'elle-même au paiement.
 */
export function useAttendreReglement() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, attendre }: { id: string; attendre: boolean }) =>
      api.put<CommandeDetaillee>(`/api/commandes/${id}/reglement/attente`, { attendre }),
    onSuccess: (_d, v) => invalider(v.id),
  });
}

/** Envoie au client une relance de règlement relue. */
export function useRelancerReglement() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, corps }: {
      id: string;
      corps: { destinataire: string; objet: string; message: string };
    }) => api.post<{ commande: CommandeDetaillee; brouillon: EtatBrouillonRelance }>(
      `/api/commandes/${id}/reglement/relance`, corps,
    ),
    onSuccess: (_d, v) => invalider(v.id),
  });
}

/** Les relances de règlement envoyées, relues dans Outlook. */
export function useRelancesReglement(id: string, enabled = true) {
  return useQuery({
    queryKey: ['relances-reglement', id] as const,
    queryFn: () => api.get<MailEnvoiLu[]>(`/api/commandes/${id}/reglement/relances`),
    staleTime: 0,
    enabled,
  });
}

/**
 * Coche la case : programme la demande pour trois jours après la livraison.
 *
 * Rien ne part à cet instant — c'est une DATE qu'on pose, et le passage de
 * 10 h l'honore le jour venu. La commande rendue porte l'échéance retenue.
 */
export function useDemandeAvis() {
  const invalider = useInvalidation();
  return useMutation({
    // `telephone` n'accompagne la demande que lorsque celui de la fiche est
    // inutilisable : l'API refuse de programmer sans numéro composable dès
    // lors qu'il y en a un d'enregistré.
    mutationFn: ({ id, telephone }: { id: string; telephone?: string }) =>
      api.post<CommandeDetaillee>(`/api/commandes/${id}/avis`, telephone ? { telephone } : {}),
    onSuccess: (_d, v) => invalider(v.id),
  });
}

/** Décoche. Refusé par l'API une fois la demande partie. */
export function useAnnulerAvis() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => api.delete<CommandeDetaillee>(`/api/commandes/${id}/avis`),
    onSuccess: (_d, v) => invalider(v.id),
  });
}

/**
 * Relève le suivi auprès du transporteur.
 *
 * Interroge le transporteur DIRECTEMENT — plus d'agrégateur. Rend la commande
 * telle qu'elle est après relevé : inchangée si le transporteur n'avait rien de
 * neuf, ce qui est le cas le plus fréquent.
 */
export function useReleverSuivi() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, codePostal }: { id: string; codePostal?: string }) =>
      api.post<CommandeDetaillee>(
        `/api/commandes/${id}/suivi/relever`,
        codePostal ? { codePostal } : {},
      ),
    onSuccess: (_d, v) => invalider(v.id),
  });
}

/** Ce que rend une actualisation globale : de quoi écrire une phrase, pas un tableau. */
export interface BilanActualisation {
  /** Portail fournisseur : ce qu'il a appris sur les commandes Fortemix. */
  fortemix: {
    examinees: number;
    datesTrouvees: number;
    trackingsTrouves: number;
    introuvables: number;
    echecs: number;
  };
  /** Relevé transporteur : ce que les transporteurs ont répondu. */
  suivi: { examinees: number; misesAJour: number; echecs: number };
  /** Relevé du règlement chez Sellsy : c'est lui qui débloque l'envoi fournisseur. */
  paiement: {
    examinees: number;
    relevees: number;
    regleesMaintenant: number;
    introuvables: string[];
    erreurs: Array<{ ref: string; message: string }>;
    /** `null` quand Sellsy est configuré ; la raison sinon. */
    indisponible: string | null;
  };
}

/**
 * Actualise TOUT : le règlement des factures, le portail fournisseur, puis les
 * transporteurs.
 *
 * Même périmètre et même code que les passages automatiques — le bouton ne doit
 * pas pouvoir donner un résultat que les horaires ne donneraient pas. L'ordre
 * est décidé côté serveur : le règlement d'abord, parce qu'il débloque l'envoi
 * au fournisseur et qu'il est le plus rapide ; puis Fortemix, qui découvre les
 * numéros de suivi ; puis les transporteurs, qui les interrogent dans la
 * foulée.
 *
 * Les transporteurs sont interrogés en série, avec un Chromium à lancer : le
 * bouton met plusieurs dizaines de secondes à rendre la main sur un lot de
 * commandes. L'appel n'a donc pas de délai côté client, et l'écran doit dire
 * qu'il travaille — un bouton muet pendant une minute passe pour une panne.
 */
export function useReleverTous() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<BilanActualisation>('/api/commandes/actualiser', {}),
    onSuccess: () => {
      // Tout peut avoir bougé : statuts, étapes, actions calculées, journal.
      void client.invalidateQueries({ queryKey: ['commandes'] });
      void client.invalidateQueries({ queryKey: ['commande'] });
      void client.invalidateQueries({ queryKey: ['journal'] });
      void client.invalidateQueries({ queryKey: cles.sante });
    },
  });
}

/** Ce qu'un relevé Fortemix sur UNE commande a donné. */
export interface ResultatFortemixCommande {
  commande: CommandeDetaillee;
  dateTrouvee: boolean;
  trackingTrouve: boolean;
  /** `true` quand aucune des formes d'identifiant ne trouve la commande. */
  introuvable: boolean;
  /**
   * Ce que le portail annonce, ÉCRIT OU NON.
   *
   * Sert à nommer un écart : Fortemix peut publier une date différente de celle
   * déjà saisie, qu'on ne remplace pas. Dire « rien de neuf » dans ce cas serait
   * faux, et ferait chercher une panne du portail là où il n'y en a pas.
   */
  annonce: { dateExpedition: string | null; tracking: string | null } | null;
}

/**
 * Relève le portail Fortemix pour une commande.
 *
 * Mêmes règles d'écriture que le passage de 8 h 05 et 13 h 05 : une date ou un
 * tracking absent est rempli, et corrigé s'il diffère du portail, qui fait foi.
 *
 * Et le même effet : une découverte part aussitôt chez le client — la date par
 * le mail d'expédition, le tracking par le mail de suivi —, sans relecture.
 */
export function useReleverFortemix() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<ResultatFortemixCommande>(`/api/commandes/${id}/fortemix/relever`, {}),
    onSuccess: (_d, id) => invalider(id),
  });
}

/** Une palette, telle qu'elle est relue avant la réservation. */
export interface PaletteSaisie {
  longueur: number;
  largeur: number;
  hauteur: number;
  poids: number;
}

export interface ResultatEnlevement {
  reserve: boolean;
  simule: boolean;
  /** DHL Freight pour des palettes, DPD pour des colis. */
  transport: 'dhl' | 'dpd';
  /** Le transporteur imposé par le mail de HIOLLE, ou `null` : l'écran choisit alors. */
  transportLu: 'dhl' | 'dpd' | null;
  instruction: {
    body: Record<string, unknown>;
    avertissements: string[];
    resume: {
      palettes: number;
      poidsTotal: number;
      volumeTotal: number;
      metresPlancher: number;
      enlevement: string;
      livraison: string;
      codeProduit: string;
      /** `standard` : groupage (SPD/ECI) ; `direct` : lot partiel (ELD/ELI). */
      gamme: 'standard' | 'direct';
      international: boolean;
    };
  } | null;
  /** La demande DPD, quand `transport` vaut `dpd`. */
  instructionDpd: {
    avertissements: string[];
    resume: { colis: number; poidsTotal: number; enlevement: string; international: boolean };
  } | null;
  /** Ce qui empêche de composer. Vide quand une instruction existe. */
  manques: string[];
  /** Un enlèvement semble déjà demandé hors de l'outil : pourquoi on le pense. */
  dejaDemande: string | null;
  /** La commande a déjà sa demande d'enlèvement : plus aucune réservation, essai compris. */
  verrouille?: string | null;
  adresses: {
    enlevement: { nom: string; ligne: string; ville: string; codePostal: string; pays: string };
    livraison: {
      nom: string; ligne: string | null; complement: string | null; ville: string | null;
      codePostal: string | null; pays: string | null; telephone: string | null;
    };
  };
  palettes: PaletteSaisie[];
  sourcePalettes: 'mail' | 'saisie' | 'aucune';
  /** Ce que la lecture du mail a dû supposer (poids par palette ou total). */
  avertissementsPalettes: string[];
  mailHiolle: {
    cherche: boolean; trouve: boolean; raison: string | null; extrait: string | null;
  };
  /**
   * `disponible` : le bac à sable, où se fait l'essai. `production` : la
   * réservation réelle est configurée — `raisonProduction` dit sinon pourquoi.
   */
  etatDhl: {
    disponible: boolean; raison: string | null;
    production: boolean; raisonProduction: string | null;
  };
  etatDpd: { disponible: boolean; raison: string | null; production: boolean };
  /**
   * Le dernier essai DHL en bac à sable, comparé à la demande affichée.
   * `conforme` : la production l'accepterait telle quelle.
   */
  essaiDhl: {
    envoiId: string; le: string; jourEnlevement: string | null; documents: number;
    conforme: boolean; ecarts: string[];
  } | null;
  /**
   * DHL : le mail qui partira à HIOLLE avec les documents à la réservation en
   * production — en réponse à son mail quand il est retrouvé. À relire ici.
   */
  reponseHiolle: {
    enReponse: boolean; destinataire: string; objet: string; recuLe: string | null; corpsHtml: string;
  } | null;
  envoi?: {
    id: string;
    /** Numéros de colis DPD. Vide chez DHL. */
    colis: string[];
    jourEnlevement: string | null;
    tracking: string | null;
    /** DHL : SSCC de chaque palette. */
    sscc?: string[];
    documents: number;
    environnement?: EnvironnementDhl;
    /** Les PDF d'un essai en bac à sable, à vérifier avant la production. */
    pieces?: { nom: string; contenu: string }[];
    brouillon: {
      depose: boolean; erreur: string | null; lienWeb: string | null;
      /** DHL : l'adresse de HIOLLE à qui sont partis les documents. */
      destinataire?: string | null;
      /** DHL : `true` quand c'était une réponse au mail de HIOLLE. */
      enReponse?: boolean;
    };
    /** Production : la date d'expédition inscrite (prochain jour ouvré), sans mail au client. */
    expedieLe?: string;
    /** Production : le mail de tracking parti au client, sans relecture. */
    mailTracking?: { etat: string | null; erreur: string | null } | null;
  };
}

export type EnvironnementDhl = 'bac-a-sable' | 'production';

/**
 * Demande d'enlèvement chez HIOLLE : DHL pour des palettes, DPD pour des colis.
 *
 * Le même appel sert au passage à blanc et à la réservation : c'est `reserver`
 * qui les sépare, et il vaut `false` tant qu'on ne l'a pas posé. Deux endpoints
 * auraient permis d'appeler le second sans avoir vu le premier.
 */
export function useEnlevement() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: (v: {
      id: string;
      corps: {
        palettes?: PaletteSaisie[];
        texteMail?: string;
        valeur?: number;
        instructionEnlevement?: string;
        instructionLivraison?: string;
        transport?: 'dhl' | 'dpd';
        reserver?: boolean;
        environnement?: EnvironnementDhl;
      };
    }) => api.post<ResultatEnlevement>(`/api/commandes/${v.id}/enlevement`, v.corps),
    // Seule une réservation change la commande ; un passage à blanc ne touche
    // à rien, et invalider ferait clignoter la fiche pour rien.
    onSuccess: (r, v) => { if (r.reserve) invalider(v.id); },
  });
}

/**
 * Relève le règlement d'une commande, à la demande.
 *
 * Les passages automatiques ont lieu à 8 h et 13 h, et à chaque heure pour les
 * commandes jamais relevées ; ce bouton sert entre deux.
 * Il invalide la commande : c'est le règlement qui débloque l'envoi au
 * fournisseur, et l'écran doit le montrer sans rechargement.
 */
export function useReleverPaiement() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: (id: string) => api.post<{
      trouvee: boolean;
      paiement: { statut: string; regle: boolean; releveLe: string; resteDu: number | null } | null;
      indisponible: string | null;
      message: string;
    }>(`/api/paiement/commandes/${id}/relever`, {}),
    onSuccess: (_d, id) => invalider(id),
  });
}

/** Écrit, remplace ou efface (texte vide) la note de la fiche. */
export interface AdresseSaisie {
  /** Nom du destinataire, saisi dans la demande d'enlèvement ; vide, il suit la société puis le client. */
  nom: string;
  /** Téléphone du destinataire, saisi dans la demande d'enlèvement ; vide, celui du client. */
  telephone: string;
  ligne: string; complement: string; codePostal: string; ville: string; pays: string;
}

/** Corrige l'adresse de livraison de la commande — elle fait foi partout. */
/**
 * Relit la facture chez Sellsy et reprend ce qui a changé depuis l'import :
 * société, montant, frais de port, adresse de livraison, règlement.
 */
export function useActualiserFacture() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: (id: string) => api.post<{
      commande: CommandeDetaillee; changements: string[]; avertissements: string[]; message: string;
    }>(`/api/commandes/${id}/sellsy/actualiser`, {}),
    onSuccess: (_d, id) => invalider(id),
  });
}

export function useMajAdresse() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, adresse }: { id: string; adresse: AdresseSaisie }) =>
      api.put<CommandeDetaillee>(`/api/commandes/${id}/adresse`, adresse),
    onSuccess: (_d, v) => invalider(v.id),
  });
}

export function useMajNote() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, texte }: { id: string; texte: string }) =>
      api.put<CommandeDetaillee>(`/api/commandes/${id}/note`, { texte }),
    onSuccess: (_d, v) => invalider(v.id),
  });
}

/**
 * Met une commande en attente jusqu'à un jour donné, ou lève cette attente.
 *
 * Tant que le jour n'est pas arrivé, la commande ne réclame rien et patiente
 * dans « En cours ». Le jour venu, elle revient d'elle-même dans « À faire ».
 */
export function useAttente() {
  const invalider = useInvalidation();
  const poser = useMutation({
    mutationFn: ({ id, jusquAu, motif }: { id: string; jusquAu: string; motif?: string }) =>
      api.post<CommandeDetaillee>(`/api/commandes/${id}/attente`, { jusquAu, ...(motif ? { motif } : {}) }),
    onSuccess: (_d, v) => invalider(v.id),
  });
  const lever = useMutation({
    mutationFn: (id: string) => api.post<CommandeDetaillee>(`/api/commandes/${id}/attente/lever`, {}),
    onSuccess: (_d, id) => invalider(id),
  });
  return { poser, lever };
}

/**
 * Enregistre une relance faite AUTREMENT que par le mail de l'outil.
 *
 * Toutes les relances ne passent pas par l'application : on décroche son
 * téléphone, on croise quelqu'un. Sans ce chemin, l'action revenait réclamer le
 * lendemain alors que le geste avait été fait.
 */
export function useRelanceHorsMail() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, ...corps }: {
      id: string;
      cible: 'fournisseur' | 'transporteur';
      motif?: MotifRelance;
      moyen: string;
      fournisseur?: string | null;
      commentaire?: string;
    }) => api.post<CommandeDetaillee>(`/api/commandes/${id}/relance/hors-mail`, corps),
    onSuccess: (_d, v) => invalider(v.id),
  });
}

/**
 * Écarte un mail au client : la tâche s'éteint, rien n'est envoyé.
 *
 * Une tâche qu'on ne peut pas écarter finit par être exécutée pour s'en
 * débarrasser — et c'est alors un mail dont personne ne veut qui part. La
 * commande quitte « À faire » et retombe dans « En cours ».
 */
/** Le fournisseur a été prévenu d'un remboursement ou d'une annulation Shopify. */
export function useSignalerShopify() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      api.post<CommandeDetaillee>(`/api/commandes/${id}/shopify/signale`, {}),
    onSuccess: (_d, v) => invalider(v.id),
  });
}

export function useIgnorerEnvoi() {
  const invalider = useInvalidation();
  return useMutation({
    mutationFn: ({ id, canal }: { id: string; canal: 'expedition' | 'tracking' }) =>
      api.post<CommandeDetaillee>(`/api/commandes/${id}/envois/${canal}/ignorer`, {}),
    onSuccess: (_d, v) => invalider(v.id),
  });
}

export function useMajReglages() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (corps: Record<string, unknown>) => api.patch<DocumentReglages>('/api/reglages', corps),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: cles.reglages });
      // Les seuils changent : toutes les actions calculées sont à refaire.
      void client.invalidateQueries({ queryKey: ['commandes'] });
      void client.invalidateQueries({ queryKey: ['commande'] });
    },
  });
}

export function useResoudreHubspot() {
  return useMutation({
    mutationFn: ({ email, commandeId }: { email: string; commandeId: string }) =>
      api.get<ResolutionHubspot>(`/api/hubspot/contact${requete({ email, commandeId })}`),
  });
}
